// /system-health — Platform System Health Dashboard
//
// Displays live connectivity status for the four core infrastructure services:
// Database (Aiven Postgres), Redis (Upstash), RabbitMQ (CloudAMQP), S3 (AWS).
//
// The backend /health endpoint is @Public() — no auth cookie is sent.
// This page polls it every 30 s and renders each service as a status card.
//
// Audience: SUPER_ADMIN / ADMIN / MODERATOR (platform roles only, minRoleLevel=60).
//
// Traceability: QA-ADM-455, FR-HEALTH-004 (system health dashboard).

import { useState, useEffect, useCallback } from 'react';
import { PageContainer, Spinner, Alert } from '@bbpos/ui';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { GLASS, cn } from '@/constants/design-tokens';

// ─── Types ────────────────────────────────────────────────────────────────────

type ServiceStatus = 'ok' | 'error' | 'unavailable' | 'unknown' | 'loading';

interface HealthResponse {
  status: 'ok' | 'error';
  uptime: number;
  checks: {
    database: 'ok' | 'error' | 'unavailable';
    redis: 'ok' | 'error' | 'unavailable';
    rabbitmq: 'ok' | 'error' | 'unavailable';
    s3: 'ok' | 'error' | 'unavailable';
  };
}

interface ServiceCard {
  key: keyof HealthResponse['checks'];
  label: string;
  description: string;
  iconPath: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000;

const SERVICES: ServiceCard[] = [
  {
    key: 'database',
    label: 'Database',
    description: 'Aiven PostgreSQL — primary data store',
    iconPath:
      'M4 7c0-1.657 3.582-3 8-3s8 1.343 8 3v10c0 1.657-3.582 3-8 3S4 18.657 4 17V7zm0 5c0 1.657 3.582 3 8 3s8-1.343 8-3M4 12v5',
  },
  {
    key: 'redis',
    label: 'Redis',
    description: 'Upstash Redis — sessions, OTP, rate limiting',
    iconPath:
      'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    key: 'rabbitmq',
    label: 'RabbitMQ',
    description: 'CloudAMQP — job queues and async messaging',
    iconPath:
      'M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l-3-3m3 3l3-3',
  },
  {
    key: 's3',
    label: 'S3 Storage',
    description: 'AWS S3 (ap-south-1) — uploads, backups, exports',
    iconPath:
      'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A card's state. When /health itself cannot be reached (QA 1.6.5) the cards read
 * Unknown instead of spinning on Checking… forever, or showing a stale Connected
 * next to the "Health endpoint unreachable" alert.
 */
export function cardStatus(
  health: HealthResponse | null,
  loading: boolean,
  fetchError: string | null,
  key: keyof HealthResponse['checks'],
): ServiceStatus {
  if (fetchError) return 'unknown';
  if (loading && !health) return 'loading';
  return health?.checks[key] ?? 'loading';
}

function statusTone(status: ServiceStatus): {
  bg: string;
  border: string;
  icon: string;
  badge: string;
  badgeText: string;
  label: string;
} {
  switch (status) {
    case 'ok':
      return {
        bg: 'bg-success-50 dark:bg-success-900/20',
        border: 'border-success-200 dark:border-success-700',
        icon: 'bg-success-500',
        badge: 'bg-success-100 dark:bg-success-900/40',
        badgeText: 'text-success-700 dark:text-success-300',
        label: 'Connected',
      };
    case 'error':
      return {
        bg: 'bg-danger-50 dark:bg-danger-900/20',
        border: 'border-danger-200 dark:border-danger-700',
        icon: 'bg-danger-500',
        badge: 'bg-danger-100 dark:bg-danger-900/40',
        badgeText: 'text-danger-700 dark:text-danger-300',
        label: 'Error',
      };
    case 'unavailable':
      return {
        bg: 'bg-warning-50 dark:bg-warning-900/20',
        border: 'border-warning-200 dark:border-warning-700',
        icon: 'bg-warning-500',
        badge: 'bg-warning-100 dark:bg-warning-900/40',
        badgeText: 'text-warning-700 dark:text-warning-300',
        label: 'Unavailable',
      };
    case 'unknown':
      return {
        bg: 'bg-neutral-50 dark:bg-neutral-800',
        border: 'border-neutral-300 dark:border-neutral-600',
        icon: 'bg-neutral-500',
        badge: 'bg-neutral-200 dark:bg-neutral-700',
        badgeText: 'text-neutral-700 dark:text-neutral-200',
        label: 'Unknown',
      };
    default:
      return {
        bg: 'bg-neutral-50 dark:bg-neutral-800',
        border: 'border-neutral-200 dark:border-neutral-700',
        icon: 'bg-neutral-400',
        badge: 'bg-neutral-100 dark:bg-neutral-700',
        badgeText: 'text-neutral-600 dark:text-neutral-300',
        label: 'Checking…',
      };
  }
}

function ServiceIcon({ path }: { path: string }) {
  return (
    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SystemHealthPage() {
  useRequireAuth();

  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch('/health', { cache: 'no-store' });
      if (!res.ok && res.status !== 503) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data: HealthResponse = await res.json();
      setHealth(data);
      setFetchError(null);
      setLastChecked(new Date());
    } catch (err: any) {
      setFetchError(err?.message ?? 'Could not reach health endpoint');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const timer = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchHealth]);

  const getStatus = (key: keyof HealthResponse['checks']): ServiceStatus =>
    cardStatus(health, loading, fetchError, key);

  const allHealthy = health ? health.status === 'ok' : null;

  return (
    <PageContainer
      title="System Health"
      subtitle="Live connectivity status of core infrastructure services"
      breadcrumbs={[{ label: 'System Health' }]}
    >
      {/* Hero header */}
      <div
        className={cn(
          'bg-gradient-to-r from-brand-600 to-indigo-600 rounded-xl p-6 text-white shadow-lg mb-6',
          'animate-fade-in',
        )}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Infrastructure Status</h2>
            <p className="text-brand-100 text-sm mt-0.5">
              Polls the <code className="bg-white/20 rounded px-1 text-xs">/health</code> endpoint every 30 seconds
            </p>
          </div>
          <div className="flex items-center gap-3">
            {allHealthy === true && (
              <span className="flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1 text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-success-400 animate-pulse" />
                All systems operational
              </span>
            )}
            {allHealthy === false && (
              <span className="flex items-center gap-1.5 bg-danger-500/30 rounded-full px-3 py-1 text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-danger-300" />
                Degraded
              </span>
            )}
            <button
              onClick={fetchHealth}
              className="bg-white/20 hover:bg-white/30 transition-colors rounded-lg px-3 py-1.5 text-sm font-medium"
              aria-label="Refresh health status"
            >
              Refresh
            </button>
          </div>
        </div>
        {lastChecked && (
          <p className="text-brand-200 text-xs mt-3">
            Last checked: {lastChecked.toLocaleTimeString('en-IN')}
          </p>
        )}
      </div>

      {fetchError && (
        <Alert
          variant="danger"
          className="mb-6"
          title="Health endpoint unreachable"
          description={fetchError}
        />
      )}

      {/* 4 service cards */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        aria-label="Service health cards"
      >
        {SERVICES.map((svc, i) => {
          const status = getStatus(svc.key);
          const tone = statusTone(status);

          return (
            <div
              key={svc.key}
              data-service={svc.key}
              data-status={status}
              className={cn(
                GLASS,
                'p-5 rounded-xl border transition-all duration-200 hover:shadow-md',
                tone.bg,
                tone.border,
                'animate-slide-up',
              )}
              style={{ animationDelay: `${i * 60}ms` }}
              role="article"
              aria-label={`${svc.label} status`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0', tone.icon)}>
                    <ServiceIcon path={svc.iconPath} />
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900 dark:text-white text-sm">{svc.label}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{svc.description}</p>
                  </div>
                </div>

                {status === 'loading' ? (
                  <Spinner size="sm" />
                ) : (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium flex-shrink-0',
                      tone.badge,
                      tone.badgeText,
                    )}
                    aria-label={`Status: ${tone.label}`}
                  >
                    <span
                      className={cn(
                        'w-1.5 h-1.5 rounded-full',
                        status === 'ok' ? 'bg-success-500' :
                        status === 'error' ? 'bg-danger-500' :
                        status === 'unknown' ? 'bg-neutral-500' : 'bg-warning-500',
                      )}
                    />
                    {tone.label}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {health && (
        <p className="text-xs text-neutral-400 dark:text-neutral-500 text-right mt-4">
          Server uptime: {Math.floor(health.uptime / 60)}m {health.uptime % 60}s
        </p>
      )}
    </PageContainer>
  );
}
